import {Checkbox, Icon, List, ListItem, NoData, Space} from '@mc/ui-v4';
import React, {ChangeEvent, Fragment, MouseEvent, ReactNode, useCallback} from 'react';
import {CatalogItemVO, LayerVO} from '../../api';
import {mcDropDown, mcDropDownBack, mcGlobe, mcList, mcTag, mcTagCross} from '@mc/react-ui-icons';
import classNames from 'classnames';
import {useAppConfig} from '@mc/react-app-base';
import './CatalogLayerTreeList.scss';
import {LayerId} from '@mc/react-mapio';
import {getLabelLayerIds as getLabelLayerIdsUtil, isParentOfUserDbLayers, isUserDbLayer} from '../../helpers/LayerConfigUtils';
import {useMapioContext} from '../../mapioContext';
import {isExternalLayer} from '../../helpers/ExternalLayerData';

type CheckedLayers = {[key: LayerId]: boolean};

type Props = {
    activeLayers: LayerId[];
    data: CatalogItemVO[];
    expandedLayerIdList: LayerId[];
    layersConfig: {[key: LayerId]: LayerVO};
    level: number;
    onChange: (activeLayers: LayerId[]) => void;
    onClickLayerButton: (activeLayers: LayerId) => void;
    setActiveLayers: React.Dispatch<React.SetStateAction<LayerId[]>>;
    setExpandedLayerIdList: React.Dispatch<React.SetStateAction<LayerId[]>>;
};

export const CatalogLayerTreeList: React.FC<Props> = ({
    activeLayers,
    data,
    expandedLayerIdList,
    layersConfig,
    level,
    onChange,
    onClickLayerButton,
    setActiveLayers,
    setExpandedLayerIdList,
}) => {
    // @ts-ignore
    const {serverPublic} = useAppConfig();
    const {externalLayers} = useMapioContext().value;

    const activeLayerIdSet = React.useMemo(() => new Set(activeLayers.map(id => Number(id))), [activeLayers]);

    /**
     * Vrátí kandidátní ID popisných vrstev pro danou vrstvu.
     * U diskriminovaných vrstev preferuje kandidáty se stejným parentLayerId.
     * Sdílená logika s SearchPanel filtrem (viz {@link getLabelLayerIds}).
     */
    const getLabelLayerIds = useCallback((layerId: LayerId): number[] => getLabelLayerIdsUtil(layersConfig, layerId), [layersConfig]);

    /** Aplikuje nové activeLayers do state i do parent callbacku. */
    const applyChange = useCallback(
        (next: LayerId[]): void => {
            setActiveLayers(next);
            onChange(next);
        },
        [setActiveLayers, onChange],
    );

    /** Shromáždí ID všech listových vrstev z katalogu (rekurzivně). */
    const collectLayerIds = (item: CatalogItemVO): LayerId[] => {
        if (item.layerId && !item.children?.length) return [item.layerId];
        return (item.children ?? []).flatMap(collectLayerIds);
    };

    const handleLayerChange = useCallback(
        (item: CatalogItemVO, checked: boolean): void => {
            const ids = collectLayerIds(item);
            let next = [...activeLayers];
            const labelIds = [...new Set(ids.flatMap(getLabelLayerIds))];
            if (checked) {
                // Zapnout vrstvu i její label vrstvy (symetricky s vypnutím)
                [...ids, ...labelIds].forEach(id => {
                    if (!next.includes(id)) next.push(id);
                });
            } else {
                // Vypnout vrstvu i její label vrstvy
                const toRemove = new Set([...ids.map(String), ...labelIds.map(String)]);
                next = next.filter(id => !toRemove.has(String(id)));
            }
            applyChange(next);
        },
        [activeLayers, getLabelLayerIds, applyChange],
    );

    const handleOpenAttributeTable = useCallback((layerId: LayerId, event: MouseEvent<SVGElement>): void => {
        event.stopPropagation();
        onClickLayerButton(layerId);
    }, []);

    const handleToggleLabelLayer = useCallback(
        (layerId: LayerId, event: MouseEvent<SVGElement>): void => {
            event.stopPropagation();
            const labelIds = getLabelLayerIds(layerId);
            if (!labelIds.length) return;

            const hasAnyActiveLabel = labelIds.some(id => activeLayerIdSet.has(Number(id)));
            const next = hasAnyActiveLabel
                ? activeLayers.filter(id => !labelIds.includes(Number(id)))
                : activeLayers.includes(labelIds[0])
                  ? activeLayers
                  : [...activeLayers, labelIds[0]];
            applyChange(next);
        },
        [activeLayers, getLabelLayerIds, activeLayerIdSet, applyChange],
    );

    const handleExpandedLayerIdList = useCallback((id: LayerId | string, expandedLayerIdList: LayerId[], event: MouseEvent<HTMLElement>): void => {
        const target = event.target as HTMLElement;
        const tagName = target.tagName.toLowerCase();

        // Ignorovat kliknutí na checkbox, jeho label nebo jakýkoliv element uvnitř checkboxu
        if (['label', 'input'].includes(tagName) || target.closest('label') || target.closest('input[type="checkbox"]')) {
            return;
        }

        if (['div', 'svg', 'path'].includes(tagName)) {
            let expandedLayerIdListNew: LayerId[] = [...expandedLayerIdList];

            if (expandedLayerIdListNew[level] === id) {
                expandedLayerIdListNew = expandedLayerIdListNew.splice(0, level);
            } else {
                expandedLayerIdListNew[level] = id;
                expandedLayerIdListNew = expandedLayerIdListNew.splice(0, level + 1);
            }

            setExpandedLayerIdList(expandedLayerIdListNew);
        }
    }, []);

    const checkCheckedLayers = useCallback((data: CatalogItemVO, activeLayers: LayerId[]): CheckedLayers => {
        let checkedLayers: CheckedLayers = {};

        if (activeLayers.length) {
            checkCheckedChildrenLayers(data, checkedLayers, activeLayers);
        }

        return checkedLayers;
    }, []);

    const checkCheckedChildrenLayers = useCallback((data: CatalogItemVO, checkedLayers: CheckedLayers, activeLayers: LayerId[]): void => {
        if (data.layerId && !data.children?.length) {
            checkedLayers[data.layerId] = activeLayers.includes(data.layerId);
        } else if (data.children?.length) {
            data.children.forEach((item: CatalogItemVO): void => {
                checkCheckedChildrenLayers(item, checkedLayers, activeLayers);
            });
        }
    }, []);

    const getNameofLayer = useCallback((item: CatalogItemVO, checked: boolean, indeterminate): ReactNode => {
        const isStrong: boolean = checked || indeterminate;
        const layerConfig = item.layerId ? layersConfig[item.layerId] : null;

        if (isStrong) {
            return (
                <strong>
                    {item.label} {layerConfig?.publish ? <Icon icon={mcGlobe} /> : null}
                </strong>
            );
        }

        return (
            <>
                {item.label ?? layerConfig?.label} {layerConfig?.publish ? <Icon icon={mcGlobe} /> : null}
            </>
        );
    }, []);

    if (!data?.length) {
        return <NoData>Nenalezena žádná data...</NoData>;
    }

    return (
        <List className={'catalog-layer-list'} noHorizontalPadding>
            {data.map((item: CatalogItemVO) => {
                const hasChildren: boolean = Boolean(item.children?.length);
                const isExpandedLayerId: boolean = expandedLayerIdList?.[level] === item.id;
                const checkedLayersValues: boolean[] = Object.values(checkCheckedLayers(item, activeLayers));
                const checked: boolean = checkedLayersValues.length && checkedLayersValues.every((item: boolean) => item);
                const indeterminate: boolean = checked ? false : checkedLayersValues.some((item: boolean) => item);
                const labelIds = item.layerId ? getLabelLayerIds(item.layerId) : [];
                const labelActive = labelIds.some(id => activeLayerIdSet.has(Number(id)));
                return (
                    <Fragment key={`${item.id}-${item.layerId}`}>
                        <ListItem
                            className={classNames('border-0 rounded', {'cursor-pointer expanded-layer': hasChildren})}
                            onClick={(event: MouseEvent<HTMLElement>): void => {
                                handleExpandedLayerIdList(item.id, expandedLayerIdList, event);
                            }}
                            extra={
                                <Space>
                                    {!serverPublic && labelIds.length > 0 && (activeLayerIdSet.has(Number(item.layerId)) || indeterminate) && (
                                        <Icon
                                            icon={labelActive ? mcTag : mcTagCross}
                                            onClick={(event: MouseEvent<SVGElement>) => handleToggleLabelLayer(item.layerId, event)}
                                            intent={labelActive ? 'primary' : 'secondary'}
                                            className={`cursor-pointer ${labelActive ? '' : 'ui-v4-show-on-hover'}`}
                                            title={labelActive ? 'Skrýt popisky' : 'Zobrazit popisky'}
                                        />
                                    )}
                                    {!serverPublic &&
                                        ((Boolean(item.layerId) &&
                                            isUserDbLayer(layersConfig, item.layerId) &&
                                            (activeLayers.some(ac => ac === item.layerId) ||
                                                isParentOfUserDbLayers(layersConfig, activeLayers, item.layerId))) ||
                                            (isUserDbLayer(layersConfig, item.layerId) && indeterminate)) && (
                                            <Icon
                                                icon={mcList}
                                                onClick={(event: MouseEvent<SVGElement>) => handleOpenAttributeTable(item.layerId, event)}
                                                intent={'primary'}
                                                className="ui-v4-show-on-hover cursor-pointer"
                                                title={'Zobrazit atributovou tabulku'}
                                            />
                                        )}
                                    {!serverPublic && Boolean(item.layerId) && isExternalLayer(externalLayers, item.layerId) && (
                                        <Icon
                                            icon={mcList}
                                            onClick={(event: MouseEvent<SVGElement>) => handleOpenAttributeTable(item.layerId, event)}
                                            intent={'primary'}
                                            className="ui-v4-show-on-hover cursor-pointer"
                                            title={'Zobrazit atributovou tabulku'}
                                        />
                                    )}
                                    {hasChildren && <Icon icon={isExpandedLayerId ? mcDropDownBack : mcDropDown} intent={'primary'} />}
                                </Space>
                            }
                            key={`${item.id}-${item.layerId}`}
                        >
                            <Checkbox
                                checked={indeterminate ? undefined : checked}
                                indeterminate={indeterminate && !checked}
                                key={`${item.id}-${item.layerId}`}
                                onChange={(event: ChangeEvent<HTMLInputElement>): void => {
                                    handleLayerChange(item, event.target.checked);
                                }}
                            >
                                {getNameofLayer(item, checked, indeterminate)}
                            </Checkbox>
                        </ListItem>
                        {hasChildren && isExpandedLayerId && (
                            <CatalogLayerTreeList
                                activeLayers={activeLayers}
                                data={item.children}
                                expandedLayerIdList={expandedLayerIdList}
                                layersConfig={layersConfig}
                                level={level + 1}
                                onChange={onChange}
                                onClickLayerButton={onClickLayerButton}
                                setActiveLayers={setActiveLayers}
                                setExpandedLayerIdList={setExpandedLayerIdList}
                            />
                        )}
                    </Fragment>
                );
            })}
        </List>
    );
};
